/**
 * screens/IntentsScreen.tsx
 * Controls · Intents (operator) — toggle what the agent can do within plan.
 * Recreated verbatim from the design HTML (~174-239), max-width 960.
 *
 * Wired to sk-orchestrator: GET/PUT /v1/voice-console/controls/intents +
 * /enrichment (voiceService listTenantIntents/setIntentEnabled/setEnrichment).
 * Backend is source of truth for per-intent state (LIVE|LOCKED|OFF) and the
 * enrichment on/off flags; falls back to mock (demo data) when no backend/auth.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, Pressable, ActivityIndicator } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useConsole } from '../context/voiceConsoleContext';
import Icon from '../components/icon';
import { Chip, Switch, ScreenBody } from '../components/voiceUi';
import { listTenantIntents, setIntentEnabled, setEnrichment } from '../services/voice/voiceService';
import { humanize, humanizeTool, intentTitle } from '../utils/format';
import { showToast } from '../components/toastComponent';
import { handleApiError } from '../utils/errorHandler';

// Common view-model the UI renders. Both the live backend shape and the mock
// CATALOG fallback normalize into this, so markup below stays identical.
type IntentVM = {
  intentId: string;
  slug: string;
  title: string;
  category: string;
  desc: string;
  spine: string;
  ownerLabel: string;
  state: 'live' | 'locked' | 'off';
  enrich: { name: string; on: boolean }[];
};

// Human owner label from backend kind/ref (mock rows already carry ownerLabel).
function ownerLabelOf(row: any): string {
  if (row.ownerLabel) return row.ownerLabel;
  const ref = row.ownerRef ?? row.owner ?? '';
  if (row.ownerKind === 'VERTICAL') return `${humanize(ref)} vertical`;
  return `SK ${humanize(ref)}`;
}

// Normalize one row (live backend OR mock CATALOG) → IntentVM.
function toVM(row: any): IntentVM {
  // Live rows carry state:'LIVE'|'LOCKED'|'OFF' + enrichment:[{toolName,on}].
  // Mock rows carry enrich:string[] and no state (all treated as off unless seeded).
  const hasState = typeof row.state === 'string';
  const state: IntentVM['state'] = hasState
    ? (row.state === 'LIVE' ? 'live' : row.state === 'LOCKED' ? 'locked' : 'off')
    : 'off';
  const enrich = Array.isArray(row.enrichment)
    ? row.enrichment.map((e: any) => ({ name: e.toolName, on: !!e.on }))
    : Array.isArray(row.enrich)
      ? row.enrich.map((name: string) => ({ name, on: true }))
      : [];
  return {
    // Backend toggles look up by the real intent id (voice-controls returns
    // `intentId`); slug is display-only. Keep both — sending slug 404s.
    intentId: row.intentId ?? row.id ?? row.slug,
    slug: row.slug,
    // Clean caller-facing title (backend display_name), slug fallback.
    title: intentTitle({ displayName: row.displayName, slug: row.slug }),
    category: row.category,
    desc: row.description ?? row.desc ?? '',
    spine: row.spineTool ?? row.spine ?? '',
    ownerLabel: ownerLabelOf(row),
    state,
    enrich,
  };
}

export default function IntentsScreen() {
  const { theme } = useTheme();
  const c = theme.colors;
  const { orgId } = useConsole();

  const [rows, setRows] = useState<IntentVM[]>([]);
  const [loading, setLoading] = useState(true);
  const [live, setLive] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  // Which intents have their "Advanced" (per-tool) panel expanded. Collapsed by
  // default — a normal operator toggles the capability, not its internal tools.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpanded = (id: string) =>
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const load = useCallback(async () => {
    setLoading(true);
    const { data, live: isLive } = await listTenantIntents();
    setRows((data ?? []).map(toVM));
    setLive(isLive);
    setLoading(false);
  }, []);

  // Plain mount/org-change effect — the console shell has no React Navigation,
  // so useFocusEffect is NOT available here.
  useEffect(() => { void load(); }, [load, orgId]);

  const liveCount = rows.filter(i => i.state === 'live').length;
  const lockedCount = rows.filter(i => i.state === 'locked').length;

  const cats = [...new Set(rows.map(i => i.category))];

  // Toggle intent on/off. 403 INTENT_LOCKED → upsell toast (plan gate); reload
  // afterward so the row reflects the backend's authoritative state.
  const toggleIntent = async (i: IntentVM) => {
    if (busy) return;
    setBusy(i.slug);
    try {
      await setIntentEnabled(i.intentId, i.state !== 'live');
      await load();
    } catch (err) {
      const e = err as any;
      const status = e?.response?.status;
      const code = e?.response?.data?.code ?? e?.response?.data?.error?.code;
      if (status === 403 || code === 'INTENT_LOCKED') {
        showToast('error', 'Not in your plan', `${i.title} requires ${i.ownerLabel}. Upgrade to enable it.`);
      } else {
        handleApiError(err, 'Could not update intent');
      }
    } finally {
      setBusy(null);
    }
  };

  // Toggle a single enrichment tool for a live intent, then reload.
  const toggleEnrich = async (i: IntentVM, name: string, nextOn: boolean) => {
    if (busy) return;
    setBusy(i.slug + '::' + name);
    try {
      await setEnrichment(i.intentId, name, nextOn);
      await load();
    } catch (err) {
      handleApiError(err, 'Could not update enrichment');
    } finally {
      setBusy(null);
    }
  };

  return (
    <ScreenBody maxWidth={960}>
      {/* Chip row */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <Chip kind="solid">{liveCount} live</Chip>
        <Chip kind="outline">{lockedCount} locked</Chip>
        <View style={{ flex: 1 }} />
        {!live && !loading && (
          <Text style={{ fontSize: 11.5, color: c.ink4, fontFamily: theme.font.mono }}>Couldn’t load — check connection</Text>
        )}
        <Text style={{ fontSize: 11.5, color: c.ink4, fontFamily: theme.font.sans }}>
          Softknack authors the menu · you toggle within your plan
        </Text>
      </View>

      {/* Loading */}
      {loading && (
        <View style={{ padding: 40, alignItems: 'center' }}>
          <ActivityIndicator color={c.ink3} />
        </View>
      )}

      {!loading && (
      <View style={{ flexDirection: 'column', gap: 22 }}>
        {cats.map(cat => (
          <View key={cat}>
            <Text style={{ fontSize: 11, fontWeight: '600', letterSpacing: 0.66, textTransform: 'uppercase', color: c.ink4, marginBottom: 9, fontFamily: theme.font.sans }}>{cat}</Text>
            <View style={{ flexDirection: 'column', gap: 10 }}>
              {rows.filter(i => i.category === cat).map(i => {
                const st = i.state;
                const locked = st === 'locked';
                const live = st === 'live';
                return (
                  <View key={i.slug} style={{ backgroundColor: c.card, borderWidth: 1, borderColor: c.line, borderRadius: 10, overflow: 'hidden', opacity: locked ? 0.62 : 1, ...(theme.shadows.sm as object) }}>
                    <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 13, paddingHorizontal: 16, paddingVertical: 14 }}>
                      <View style={{ flex: 1, minWidth: 0 }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 9, marginBottom: 3 }}>
                          <Text style={{ fontWeight: '600', fontSize: 13.5, color: c.ink, fontFamily: theme.font.sans }}>{i.title}</Text>
                          <Chip kind={live ? 'solid' : locked ? 'outline' : 'default'}>{live ? 'Live' : locked ? 'Locked' : 'Off'}</Chip>
                        </View>
                        <Text style={{ fontSize: 12.5, color: c.ink3, fontFamily: theme.font.sans }}>{i.desc}</Text>
                      </View>
                      <View style={{ flexShrink: 0, paddingTop: 2 }}>
                        {locked ? (
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, height: 30, paddingHorizontal: 12, borderRadius: 7, backgroundColor: 'transparent', borderWidth: 1, borderColor: c.lineStrong }}>
                            <Icon name="lock" size={13} color={c.ink2} />
                            <Text style={{ fontSize: 12, fontWeight: '500', color: c.ink2, fontFamily: theme.font.sans }}>Requires {i.ownerLabel}</Text>
                          </View>
                        ) : (
                          <Switch on={live} onPress={() => void toggleIntent(i)} />
                        )}
                      </View>
                    </View>
                    {live && i.enrich.length > 0 && (
                      <View style={{ borderTopWidth: 1, borderTopColor: c.line, backgroundColor: c.paper2 }}>
                        {/* Collapsed by default — a user is just a user; the tools
                            that power a capability are hidden behind Advanced. */}
                        <Pressable
                          onPress={() => toggleExpanded(i.intentId)}
                          style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 16, paddingVertical: 10 }}
                        >
                          <Icon name={expanded.has(i.intentId) ? 'chev-d' : 'chev-r'} size={13} color={c.ink4} />
                          <Text style={{ fontSize: 11, fontWeight: '600', letterSpacing: 0.55, textTransform: 'uppercase', color: c.ink4, fontFamily: theme.font.sans }}>
                            Advanced ({i.enrich.length})
                          </Text>
                        </Pressable>
                        {expanded.has(i.intentId) && (
                          <View style={{ paddingHorizontal: 16, paddingBottom: 12, paddingTop: 2, flexDirection: 'row', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                            {i.enrich.map(e => (
                              <Pressable key={e.name} onPress={() => void toggleEnrich(i, e.name, !e.on)} style={{ flexDirection: 'row', alignItems: 'center', gap: 7 }}>
                                <Switch on={e.on} sm />
                                <Text style={{ fontSize: 12, color: c.ink2, fontFamily: theme.font.sans }}>{humanizeTool(e.name)}</Text>
                              </Pressable>
                            ))}
                          </View>
                        )}
                      </View>
                    )}
                  </View>
                );
              })}
            </View>
          </View>
        ))}
      </View>
      )}
    </ScreenBody>
  );
}
